import {useMutation} from '@apollo/client'
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck'
import {
  Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {first} from 'lodash-es'
import {FC, useContext, useMemo, useState} from 'react'
import {useNotify, useTranslate} from 'react-admin'
import {Link} from 'react-router-dom'

import ConfirmationDialog from '../../components/ConfirmationDialog'
import {
  BlockIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  FlagIcon,
  LogoutIcon,
  MagicIcon,
  MoreVertIcon, PictureIcon,
  UserAddIcon,
} from '../../components/icons'
import {SandboxContext} from '../../contexts/SandboxContext'
import useUpdateChatQueries from '../../hooks/chat/useUpdateChatsQueries'
import useHasPermission from '../../hooks/useHasPermission'
import useTracker from '../../hooks/useTracker'
import {Chats, MutationRoot} from '../../types/graphqlSchema'
import {CHAT_FIELDS} from '.'
import {BulkSelectionContext} from './BulkSelectionContext'
import ChatSummaryDialog from './ChatSummaryDialog'

const ChatContextMenuButton: FC<ChatContextMenuButtonProps> = ({
  chat,
  isExternalMultiMerchantUserChat,
  isInChatList,
  onOpenChatMessageFileAttachmentListSidebar,
}) => {
  const {assignedMerchantUser, id, isArchived, isSpam, owningUser, userChats} = chat
  const {onToggleChatSelected} = useContext(BulkSelectionContext)
  const {isSandbox} = useContext(SandboxContext)
  const translate = useTranslate()
  const notify = useNotify()
  const {trackEvent} = useTracker()
  const classes = useStyles()
  const canCreateMerchantCustomerUsers = useHasPermission(
    'create', 'merchant_customer_users'
  )
  const updateQueries = useUpdateChatQueries(id, CHAT_FIELDS)
  const [isChatSummaryDialogOpen, setIsChatSummaryDialogOpen] = useState(false)
  const [archive] = useMutation<MutationRoot['update_chats_by_pk']>(
    UPDATE_CHAT_MUTATION,
    {
      optimisticResponse: {
        // @ts-ignore
        update_chats_by_pk: {__typename: 'chats', id, isArchived: !isArchived, isSpam},
      },
      updateQueries,
      variables: {chatId: id, setInput: {isArchived: !isArchived}},
    }
  )
  const [reportSpam] = useMutation<MutationRoot['update_chats_by_pk']>(
    UPDATE_CHAT_MUTATION, {
      onCompleted: () => {
        setIsSpamReportDialogOpen(false)
        notify('chat.external.contextMenu.spam.success')
      },
      variables: {chatId: id, setInput: {isSpam: true}},
    }
  )
  const isFlagged = useMemo(() => first(userChats)?.isFlagged, [userChats])
  const [anchorElement, setAnchorElement] = useState<HTMLElement>()
  const [toggleFlagged] = useMutation(
    USER_CHATS_UPSERT_MUTATION,
    {
      optimisticResponse: {insert_user_chats_one: {
        __typename: 'user_chats', chatId: id, isFlagged: !isFlagged,
      }},
      variables: {chatId: id, isFlagged: !isFlagged},
    },
  )
  const openMenu = event => {
    event.preventDefault()
    setAnchorElement(event.currentTarget)
  }
  const closeMenu = event => {
    event.stopPropagation()
    setAnchorElement(undefined)
  }
  const [isSpamReportDialogOpen, setIsSpamReportDialogOpen] = useState(false)
  return (
    <IconButton
      aria-controls="long-menu"
      aria-haspopup="true"
      aria-label="more"
      className={classes.root}
      onClick={openMenu}
    >
      <ConfirmationDialog
        confirmationText={translate('chat.external.contextMenu.spam.title')}
        onCancel={() => setIsSpamReportDialogOpen(false)}
        onConfirm={() => {
          reportSpam()
        }}
        open={isSpamReportDialogOpen}
        title={translate('chat.external.contextMenu.spam.title')}
      >
        {translate('chat.external.contextMenu.spam.confirm')}
      </ConfirmationDialog>
      <ChatSummaryDialog
        chatId={id}
        onClose={() => setIsChatSummaryDialogOpen(false)}
        open={isChatSummaryDialogOpen}
      />
      <MoreVertIcon />
      <Menu
        anchorEl={anchorElement}
        keepMounted
        onClose={closeMenu}
        open={!!anchorElement}
      >
        {isInChatList && [
          <MenuItem
            key="select"
            onClick={e => {
              closeMenu(e)
              onToggleChatSelected(chat)
            }}
          >
            <ListItemIcon>
              <LibraryAddCheckIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{color: 'primary', fontWeight: 600}}
            >
              {translate('chat.external.contextMenu.select')}
            </ListItemText>
          </MenuItem>,
          <Divider key="select-divider" />,
        ]}
        {!isSpam && (
          <MenuItem
            onClick={() => archive()}
          >
            <ListItemIcon>
              {isArchived ? <CloseIcon/> : <CheckIcon color="success" />}
            </ListItemIcon>
            <ListItemText>
              {isArchived ?
                translate('chat.external.contextMenu.unarchive') :
                translate('chat.external.contextMenu.archive')
              }
            </ListItemText>
          </MenuItem>
        )}
        {(isExternalMultiMerchantUserChat && assignedMerchantUser && !isArchived) &&
          <MenuItem
            onClick={() => {
              archive({
                variables: {
                  setInput: {assignedMerchantUserId: null, isArchived: true},
                },
              })
            }}
          >
            <ListItemIcon>
              <LogoutIcon className={classes.archiveAndUnassignIcon} color="info" />
            </ListItemIcon>
            <ListItemText>
              {translate('chat.external.contextMenu.archiveAndUnassign')}
            </ListItemText>
          </MenuItem>
        }
        {owningUser.customerUser?.merchantCustomerUsers.length ?
          <MenuItem
            component={Link}
            to={`/merchant_customer_users/${
              first(owningUser.customerUser?.merchantCustomerUsers)?.id
            }`}
          >
            <ListItemIcon><EditIcon color="primary" /></ListItemIcon>
            <ListItemText>
              {translate('chat.external.contextMenu.editContact')}
            </ListItemText>
          </MenuItem> :
          (canCreateMerchantCustomerUsers &&
            <MenuItem
              component={Link}
              to={`/merchant_customer_users/create?whatsappPhoneNumber=${
                encodeURIComponent(owningUser.customerUser?.whatsappPhoneNumber ?? '')
              }&firstName=${
                encodeURIComponent(owningUser.customerUser?.whatsappDisplayName ?? '')
              }`}
            >
              <ListItemIcon><UserAddIcon color="primary" /></ListItemIcon>
              <ListItemText>
                {translate('chat.external.contextMenu.addContact')}
              </ListItemText>
            </MenuItem>
          )
        }
        <MenuItem
          onClick={e => {
            closeMenu(e)
            toggleFlagged()
          }}
        >
          <ListItemIcon><FlagIcon color="warning" /></ListItemIcon>
          <ListItemText>
            {isFlagged ?
              translate('chat.external.contextMenu.removeFlag') :
              translate('chat.external.contextMenu.addFlag')
            }
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={e => {
            closeMenu(e)
            trackEvent({
              action: 'OPEN_CHAT_SUMMARIES_DIALOG',
              category: 'CHAT',
              source: 'CHAT_CONTEXT_MENU',
            })
            setIsChatSummaryDialogOpen(true)
          }}
        >
          <ListItemIcon><MagicIcon /></ListItemIcon>
          <ListItemText>
            {translate('chat.external.contextMenu.viewSummaries')}
          </ListItemText>
        </MenuItem>
        {!(isSandbox || isSpam) && (
          <MenuItem onClick={() => setIsSpamReportDialogOpen(true)}>
            <ListItemIcon><BlockIcon color="background" /></ListItemIcon>
            <ListItemText>
              {translate('chat.external.contextMenu.spam.title')}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={e => {
            closeMenu(e)
            onOpenChatMessageFileAttachmentListSidebar()
          }}
        >
          <ListItemIcon><PictureIcon color="info" /></ListItemIcon>
          <ListItemText>
            {translate('chat.external.contextMenu.viewAllMedia')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </IconButton>
  )
}

const UPDATE_CHAT_MUTATION = gql`
  mutation($chatId: uuid!, $setInput: chats_set_input!) {
    update_chats_by_pk(pk_columns: {id: $chatId}, _set: $setInput){id isArchived isSpam}
  }
`

const USER_CHATS_UPSERT_MUTATION = gql`
  mutation($chatId: uuid!, $isFlagged: Boolean!) {
    insert_user_chats_one(
      object: {chatId: $chatId, isFlagged: $isFlagged}
      on_conflict: {constraint: user_chats_pkey, update_columns: [chatId, isFlagged]}
    ){chatId isFlagged}
  }
`

const useStyles = makeStyles(() => ({
  archiveAndUnassignIcon: {
    rotate: '180deg',
  },
  root: {
    zIndex: 1,
  },
}))

interface ChatContextMenuButtonProps {
  chat: Chats
  isExternalMultiMerchantUserChat: boolean
  isInChatList?: boolean
  onOpenChatMessageFileAttachmentListSidebar: VoidFunction
}

export default ChatContextMenuButton
